import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { ArrowUpRight, Shield, Clock, Target, Handshake, ChevronRight } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const values = [
  {
    icon: Shield,
    title: "Maintain Clear Standards",
    description:
      "We make decisions based on defined principles and take responsibility for outcomes.",
  },
  {
    icon: Clock,
    title: "Commit for the Long Term",
    description:
      "We align capital, incentives, and decisions over a multi-year horizon.",
  },
  {
    icon: Target,
    title: "Decide with Discipline",
    description:
      "We evaluate opportunities through rigorous analysis and clear criteria.",
  },
  {
    icon: Handshake,
    title: "Align Interests",
    description:
      "We build transparent, long-term alignment with founders and partners.",
  },
];

const roles = [
  {
    id: "investment-analyst",
    title: "Investment Analyst",
    summary:
      "Work across sourcing, research, financial modeling, and underwriting in regulated and infrastructure-led markets.",
    responsibilities: [
      "Conduct market research and sector analysis",
      "Build and maintain financial models",
      "Support due diligence and transaction execution",
      "Prepare investment memoranda and internal materials",
      "Monitor portfolio performance and reporting",
    ],
    requirements: [
      "1\u20133 years of experience in investment banking, consulting, venture capital, or related analytical roles",
      "Strong financial modeling and analytical skills",
      "Structured thinking and clear written communication",
      "Interest in regulated markets, financial infrastructure, and enterprise software",
      "High standards of accountability and detail orientation",
    ],
  },
  {
    id: "investment-associate",
    title: "Investment Associate",
    summary:
      "Support transaction execution, portfolio engagement, and capital allocation decisions alongside senior team members.",
    responsibilities: [
      "Lead financial analysis and underwriting processes",
      "Coordinate due diligence across legal, financial, and commercial streams",
      "Contribute to investment theses and sector research",
      "Support follow-on strategy and capital deployment planning",
      "Engage with portfolio leadership on governance and reporting",
    ],
    requirements: [
      "3\u20136 years of experience in venture capital, private equity, investment banking, or strategy",
      "Demonstrated experience in transaction execution",
      "Strong financial and commercial judgment",
      "Clear communication and decision-making discipline",
      "Long-term orientation and alignment with structured capital deployment",
    ],
  },
  {
    id: "portfolio-associate",
    title: "Portfolio Associate",
    summary:
      "Support portfolio companies across governance, reporting, and operating cadence.",
    responsibilities: [
      "Assist with KPI tracking and reporting frameworks",
      "Support board preparation and governance documentation",
      "Coordinate operational initiatives with portfolio leadership",
      "Contribute to capital planning and follow-on readiness",
      "Facilitate cross-portfolio knowledge sharing where relevant",
    ],
    requirements: [
      "Experience in venture capital, consulting, enterprise operations, or finance",
      "Strong organizational and analytical skills",
      "Ability to work across multiple companies and stakeholders",
      "Structured approach to problem-solving",
      "Professional communication and execution discipline",
    ],
  },
];

export default function Careers() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, margin: "-40px" });
  const valuesRef = useRef(null);
  const valuesInView = useInView(valuesRef, { once: true, margin: "-80px" });
  const [activeRole, setActiveRole] = useState(0);
  const role = roles[activeRole];

  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      <Navbar />

      {/* ═══════ HERO ═══════ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-32 md:pt-40 pb-[15px] md:pb-[45px]"
        data-testid="careers-hero"
        style={{
          background:
            "linear-gradient(180deg, #f8faff 0%, #f3f5fb 50%, #f8faff 100%)",
        }}
      >
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />
        <motion.div
          className="absolute pointer-events-none"
          style={{ top: "5%", left: "-8%" }}
          animate={{ x: [0, 10, 0], y: [0, -6, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        >
          <div
            className="w-[500px] h-[600px] md:w-[600px] md:h-[700px] rounded-full blur-[140px]"
            style={{ background: "radial-gradient(circle, rgba(79,107,255,0.12) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)" }}
          />
        </motion.div>
        <motion.div
          className="absolute pointer-events-none"
          style={{ bottom: "-10%", right: "-6%" }}
          animate={{ x: [0, -8, 0], y: [0, 10, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        >
          <div
            className="w-[400px] h-[500px] md:w-[500px] md:h-[600px] rounded-full blur-[140px]"
            style={{ background: "radial-gradient(circle, rgba(123,97,255,0.10) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)" }}
          />
        </motion.div>
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div className="w-8 h-px bg-[#4F6BFF]" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#4F6BFF]/80">Careers</span>
            <div className="w-8 h-px bg-[#4F6BFF]" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-6"
            data-testid="careers-heading"
          >
            Build your career with Nebula Flow
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-[660px] mx-auto"
            data-testid="careers-intro"
          >
            Nebula Flow is building a focused early-stage investment platform.
            We seek individuals who combine analytical rigor, structured thinking,
            and long-term perspective.
          </motion.p>
        </div>
      </section>

      {/* ═══════ BLOCK 1: WHY WORK AT NEBULA FLOW ═══════ */}
      <section className="relative py-[30px] md:py-[50px]" data-testid="why-work-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div
              className="structured-bg wave-lines rounded-[2rem] px-10 py-12 md:px-14 md:py-16 overflow-hidden relative"
              data-testid="why-work-card"
            >
              <h2 className="relative font-body text-[30px] sm:text-[45px] font-semibold text-white tracking-tight leading-tight mb-4">
                Why work at Nebula Flow?
              </h2>
              <p className="relative font-body text-[15px] md:text-base text-white/70 leading-relaxed mb-10 max-w-lg">
                We believe in empowering our people to do their best work.
              </p>

              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-7">
                {[
                  "Direct exposure to early-stage investment decisions",
                  "Structured development within a focused team",
                  "Engagement with regulated and infrastructure-led businesses",
                  "Long-term orientation and accountability",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <ChevronRight className="w-4 h-4 text-[#7B9AFF] mt-0.5 flex-shrink-0" />
                    <span className="font-body text-[15px] text-white/90 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ BLOCK 2: CULTURAL VALUES ═══════ */}
      <section className="relative py-[30px] md:py-[50px]" data-testid="values-section" ref={valuesRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-[36px] md:mb-14"
          >
            <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-[#08122a] tracking-tight mb-3">
              Our Cultural Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="values-grid">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                data-testid={`value-card-${i}`}
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                animate={valuesInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
                className="card-gradient-line rounded-2xl border border-black/[0.04] p-7 cursor-default h-full bg-white"
                style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100/60 flex items-center justify-center mb-5">
                  <val.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-body text-[16px] font-semibold text-[#08122a] tracking-tight mb-2">
                  {val.title}
                </h3>
                <p className="font-body text-[14px] leading-[1.65] text-[#475569]">
                  {val.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ BLOCK 3: OPEN ROLES — PILL SELECTOR + DETAIL ═══════ */}
      <section className="relative pt-[30px] pb-[60px] md:py-[50px]" data-testid="open-roles-section">
        {/* Soft radial glow behind tabs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none" style={{ width: '700px', height: '400px' }}>
          <div className="w-full h-full rounded-full blur-[120px] opacity-60" style={{ background: 'radial-gradient(ellipse, rgba(79,107,255,0.06) 0%, rgba(123,97,255,0.03) 40%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-[36px] md:mb-14">
              <h2 className="font-body text-[30px] sm:text-[45px] font-semibold text-[#08122a] tracking-tight mb-3">
                Open Roles
              </h2>
            </div>
          </Reveal>

          {/* Tab selectors */}
          <Reveal delay={0.06}>
            <div className="flex items-center justify-center gap-2.5" data-testid="role-selector">
              {roles.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => setActiveRole(i)}
                  data-testid={`role-pill-${r.id}`}
                  className={`px-6 py-2.5 rounded-full text-[14px] font-medium transition-all duration-300 border ${
                    activeRole === i
                      ? "bg-[#EEF2FF] text-[#3B5BDB] border-[#DDE3FE]"
                      : "bg-white text-[#475569] border-slate-200 hover:bg-[#f8faff] hover:border-slate-300 hover:text-[#08122a]"
                  }`}
                >
                  {r.title}
                </button>
              ))}
            </div>
            <div className="mt-8 mb-8 h-px" style={{ background: 'rgba(0,0,0,0.06)' }} />
          </Reveal>

          {/* Role detail */}
          <AnimatePresence mode="wait">
            <motion.div
              key={role.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`role-detail-${role.id}`}
            >
              {/* Title + summary */}
              <div className="text-center max-w-4xl mx-auto mb-10">
                <h3 className="font-body text-[26px] md:text-[30px] font-semibold text-[#08122a] tracking-tight mb-3">
                  {role.title}
                </h3>
                <p className="font-body text-[15px] leading-[1.75] text-[#475569]">
                  {role.summary}
                </p>
              </div>

              {/* Two-column cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                {/* Key Responsibilities */}
                <motion.div
                  initial={{ opacity: 0, y: 60, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
                  className="card-gradient-line rounded-2xl border border-black/[0.04] bg-white p-5 md:p-6 cursor-default"
                  style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
                  data-testid="responsibilities-card"
                >
                  <h4 className="font-body text-[16px] font-semibold text-[#08122a] tracking-tight mb-5">
                    Key Responsibilities
                  </h4>
                  <ul className="space-y-4">
                    {role.responsibilities.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-[6px] h-[6px] rounded-full bg-[#4F6BFF]/50 flex-shrink-0 mt-[9px]" />
                        <span className="font-body text-[15px] leading-[1.7] text-[#334155]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Requirements */}
                <motion.div
                  initial={{ opacity: 0, y: 60, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
                  className="card-gradient-line rounded-2xl border border-black/[0.04] bg-white p-5 md:p-6 cursor-default"
                  style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
                  data-testid="requirements-card"
                >
                  <h4 className="font-body text-[16px] font-semibold text-[#08122a] tracking-tight mb-5">
                    Requirements
                  </h4>
                  <ul className="space-y-4">
                    {role.requirements.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-[6px] h-[6px] rounded-full bg-[#4F6BFF]/50 flex-shrink-0 mt-[9px]" />
                        <span className="font-body text-[15px] leading-[1.7] text-[#334155]">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <a
                  href="/contact"
                  data-testid={`apply-btn-${role.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 text-[14px] font-semibold text-white rounded-full transition-all duration-200 group"
                  style={{ background: "linear-gradient(135deg, #2563EB 0%, #4338CA 50%, #3730A3 100%)" }}
                >
                  Submit application
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <Footer />
    </div>
  );
}
